import { useState, useRef, useEffect, memo, useCallback } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../utils/Auth';
import API from '../../../API';
import styles from './QrDragNDrop.module.css';

const Category = ({ category, onCategoryAction }) => {

    const { id, name } = category;
    const isUncategorized = id === 'Uncategorized';

    const { listeners, transform, transition, isDragging, setNodeRef, setActivatorNodeRef } = useSortable({
        id: category.id
    });

    const style = transform ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    } : undefined;

    const containerRef = useRef(null);
    const [show, setShow] = useState('');
    const [categoryName, setCategoryName] = useState(name);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAction = async () => {
        setLoading(true);
        setError('');

        try {
            if (show === 'delete') {
                await onCategoryAction('delete', id);
            } else {
                if (categoryName === name) {
                    setShow('');
                    setLoading(false);
                    return;
                }
                await onCategoryAction('edit', id, categoryName);
            }
            setShow('');
            setLoading(false);
            setError('');
        } catch (err) {
            console.error(err);
            setError(show === 'delete' ? 'Error deleting category' : 'Error updating category');
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                if (show !== '' && !loading) {
                    setCategoryName(name);
                    setShow('');
                }
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [containerRef, name, id, show, loading]);

    useEffect(() => {
        if (show === '' && !loading) {
            setCategoryName(name);
        }
    },[name, show, loading]);

    return (
        <div className={styles.catContainer} style={style} ref={node => { setNodeRef(node); containerRef.current = node; }} >
            <i ref={setActivatorNodeRef} {...listeners} className={`fas fa-grip ${styles.icon}`} />
            <input 
                type='text' 
                id={`input_${id}`} 
                className={isUncategorized ? `${styles.input} ${styles.disabled}` : styles.input} 
                value={show === 'delete' ? `Delete ${categoryName}?` : categoryName} 
                onChange={e => setCategoryName(e.target.value)} 
                onClick={() => {
                    if (!isUncategorized && show !== 'delete' && !loading) {
                        setShow('edit');
                    }
                }}
                readOnly={isUncategorized || show === 'delete' || loading}
                disabled={loading}
            />
            {show === 'delete' ?
                <div className={styles.iconContainer}>
                    <i 
                        className={`fas fa-xmark ${styles.icon}`} 
                        id={`cancel_icon_${id}`} 
                        onClick={() => {setShow(''); setCategoryName(name);}} 
                    />
                    <i 
                        className={loading ? `fa-duotone fa-spinner fa-spin ${styles.icon}` : `fas fa-check ${styles.icon}`} 
                        id={`confirm_delete_icon_${id}`} 
                        onClick={handleAction} 
                        disabled={loading} 
                    />
                </div>
                : show === 'edit' ?
                    <i 
                        className={loading ? `fa-duotone fa-spinner fa-spin ${styles.icon}` : `fas fa-save ${styles.icon}`} 
                        id={`save_icon_${id}`} 
                        onClick={handleAction} 
                        disabled={loading} 
                    />
                    :
                    <i 
                        className={isUncategorized ? `fas fa-xmark ${styles.icon} ${styles.disabled} ${styles.delUncategorized}` : `fas fa-xmark ${styles.icon}`} 
                        id={`show_delete_icon_${id}`} 
                        onClick={() => setShow('delete')} 
                        disabled={isUncategorized} 
                    />
            }
            {error ? <p className={styles.error}>{error}</p> : null}
        </div>
    );
};

const CategoryList = memo(({ categories, onCategoryAction }) => {
    return categories.map((category) => (
        <Category category={category} key={category.id} onCategoryAction={onCategoryAction} />
    ));
});

export default function QRDragNDrop(props) {

    const { categoryOrder, setCategoryOrder } = props;
    const { activeOrgId } = useAuth();
    const [ dndItems, setDndItems ] = useState(categoryOrder.map(cat => cat.id));

    useEffect(() => {
        const currentDndIds = dndItems.join(',');
        const newCategoryOrderIds = categoryOrder.map(cat => cat.id).join(',');

        if (currentDndIds !== newCategoryOrderIds) {
            setDndItems(categoryOrder.map(cat => cat.id));
        }
    }, [categoryOrder, dndItems]);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = dndItems.indexOf(active.id);
            const newIndex = dndItems.indexOf(over.id);

            setDndItems((items) => arrayMove(items, oldIndex, newIndex));
            const newOrderedCategories = reorder(categoryOrder, oldIndex, newIndex);
            setCategoryOrder(newOrderedCategories);
        }
    }, [dndItems, setCategoryOrder, categoryOrder]);

    const handleCategoryAction = useCallback(async (actionType, categoryId, newName = null) => {
        let arr = [ ...categoryOrder ];

        if (actionType === 'delete') {
            arr = arr.filter(cat => cat.id !== categoryId);
        } else if (actionType === 'edit' && newName !== null) {
            arr = arr.map(cat => {
                if (cat.id === categoryId) {
                    return { ...cat, name: newName };
                }
                return cat;
            });
        }

        setCategoryOrder(arr);

        const categoriesToPersist = arr.filter(cat => cat.id !== "Uncategorized");

        try {
            await API.updateCategories({ org_id: activeOrgId, categories: categoriesToPersist });
        } catch (err) {
            console.error(err);
        }
    }, [categoryOrder, setCategoryOrder]);

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} >
            <SortableContext items={dndItems} strategy={verticalListSortingStrategy} >
                <CategoryList categories={categoryOrder} onCategoryAction={handleCategoryAction} />
            </SortableContext>
        </DndContext>
    );
};